import { startTransition, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'

import { CampBackdrop } from '@/components/CampBackdrop'

import type { Variant } from './config'
import { startEmber } from './controller'
import type { SceneMode } from './controller'

interface EmberSceneProps {
  variant: Variant
  /** The square box where the page draws the mark; the embers land on it. */
  anchorRef: RefObject<HTMLElement | null>
  /** Live, poster or still deciding: the page hands its drawn mark over when live. */
  onModeChange?: (mode: SceneMode) => void
}

/**
 * The live ember scene, or its poster. `gate` fills the screen behind the
 * sign-in pages over the camp backdrop (the poster); `hearth` is the small
 * fire over an empty chat, transparent over the page. Decorative throughout.
 */
export function EmberScene({ variant, anchorRef, onModeChange }: EmberSceneProps) {
  const rootRef = useRef<HTMLDivElement>(null)
  const [mode, setMode] = useState<SceneMode>('pending')
  const notify = useRef(onModeChange)
  useLayoutEffect(() => {
    notify.current = onModeChange
  })

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    // A canvas per start: a stopped scene loses its context to free the GPU at
    // once, and a lost context cannot be handed to the next start.
    const canvas = document.createElement('canvas')
    canvas.className = 'bl-ember-canvas'
    root.appendChild(canvas)
    const stop = startEmber(canvas, {
      variant,
      anchor: () => anchorRef.current,
      land: () => rootRef.current?.querySelector('[data-land]') ?? null,
      surface: () => rootRef.current?.parentElement ?? null,
      onMode: next => startTransition(() => {
        setMode(next)
        notify.current?.(next)
      }),
    })
    return () => {
      stop()
      canvas.remove()
    }
  }, [variant, anchorRef])

  return (
    <div ref={rootRef} aria-hidden="true" data-ember={mode} className={`bl-ember bl-ember--${variant}`}>
      {variant === 'gate' && <CampBackdrop className="bl-ember-poster" />}
    </div>
  )
}
